// Datenkopie nach der Kündigung: anfordern, dann mit dem Anmeldepasswort
// herunterladen. Das Passwort verschlüsselt die ZIP — es geht nur für diesen
// einen Download an den Server und wird dort nicht gespeichert.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler, token } from '../../../gemeinsam/lib/api';
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Feld, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';

type Stand = {
  moeglich: boolean; exportBis: string | null; angefordertAm: string | null;
  freigabe: { format: 'lesbar' | 'wiederherstellung'; am: string } | null;
  zuletztGeladen: { am: string } | null;
};
const tag = (s: string) => new Date(s).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });

export default function Datenkopie() {
  const { t } = useSprache();
  const { istAdmin } = useAuth();
  const [stand, setStand] = useState<Stand | null>(null);
  const [passwort, setPasswort] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [geladen, setGeladen] = useState(false);

  const laden = useCallback(async () => {
    try { setStand(await api<Stand>('/datenkopie')); } catch { setStand(null); }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  if (!stand || (!stand.moeglich && !stand.zuletztGeladen)) return null;

  async function anfordern() {
    setLaeuft(true);
    setFehler(null);
    try { setStand(await api<Stand>('/datenkopie/anfordern', { methode: 'POST' })); }
    catch (e) { setFehler(e instanceof ApiFehler ? e.message : String(e)); }
    finally { setLaeuft(false); }
  }

  async function herunterladen(e: React.FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    setFehler(null);
    try {
      // Binärdatei: nicht über api(), das JSON erwartet.
      const antwort = await fetch(apiUrl('/api/datenkopie/herunterladen'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token() ?? ''}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwort }),
      });
      if (!antwort.ok) {
        const k = await antwort.json().catch(() => ({}));
        throw new Error(k.error || `Fehler ${antwort.status}`);
      }
      const name = decodeURIComponent((antwort.headers.get('content-disposition') || '').split("''")[1] || 'LagerPro-Datenkopie.zip');
      const url = URL.createObjectURL(await antwort.blob());
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setPasswort('');
      setGeladen(true);
      await laden();
    } catch (e) { setFehler(e instanceof Error ? e.message : String(e)); }
    finally { setLaeuft(false); }
  }

  const formatText = stand.freigabe?.format === 'wiederherstellung' ? t('kopie.formatWiederherstellung') : t('kopie.formatLesbar');

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-4 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-300">
      <p className="font-semibold text-slate-900 dark:text-slate-100">{t('kopie.titel')}</p>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {geladen && <Hinweis art="erfolg">{t('kopie.geladen')}</Hinweis>}
      <p>{t('kopie.text')}{stand.exportBis && <> {t('kopie.frist').replace('{datum}', tag(stand.exportBis))}</>}</p>
      {!istAdmin && <Hinweis art="info">{t('kopie.nurAdmin')}</Hinweis>}

      {istAdmin && stand.moeglich && !stand.angefordertAm && !stand.freigabe && (
        <Knopf variante="primaer" laedt={laeuft} onClick={anfordern}>{t('kopie.anfordern')}</Knopf>
      )}
      {istAdmin && stand.angefordertAm && !stand.freigabe && (
        <Hinweis art="info">{t('kopie.angefordert').replace('{datum}', tag(stand.angefordertAm))}</Hinweis>
      )}
      {istAdmin && stand.moeglich && stand.freigabe && (
        <form onSubmit={herunterladen} className="space-y-3">
          <Hinweis art="erfolg">{t('kopie.bereit').replace('{format}', formatText)}</Hinweis>
          <Feld label={t('kopie.passwort')} type="password" value={passwort} autoComplete="current-password"
                hinweis={t('kopie.passwortHinweis')} onChange={e => setPasswort(e.target.value)} />
          <Knopf type="submit" variante="primaer" laedt={laeuft} disabled={!passwort}>{t('kopie.herunterladen')}</Knopf>
        </form>
      )}
    </div>
  );
}
